import React, { ReactElement, useEffect, useState } from 'react'
import PaymentForm from '../Payments/PaymentForm'
import { Flex, Text, useThemeUI } from 'theme-ui'
import { Switch } from 'ui'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useGetProductsQuery } from 'store/api/billing/products'
import {
  BillingProduct,
  CommonPriceInterval,
  ProductType,
} from 'store/api/billing/payments.interface'
import { setPriceInterval } from 'store/billing/products'
import ChangePlanSummary from '../Payments/ChangePlanSummary'
import { Link } from 'gatsby'
import useStripeElements from 'components/Plans/Payments/Stripe/useStripeElements'
import { PaymentMethod, setSelectedProductPk } from 'store/billing'
import Summary from '../Payments/Summary'
import Duration from '../Payments/Duration'
import { TitleSection } from 'components/NewStaticPage'

const getPriceInterval = pricePoints =>
  pricePoints?.length === 1 ? pricePoints[0].duration : CommonPriceInterval.YEARLY

const Checkout = ({ onNextClick }: { onNextClick: () => void }): ReactElement | null => {
  const dispatch = useAppDispatch()
  const { theme } = useThemeUI()
  const { stripe } = useStripeElements()
  const stripeErrorMessage = !stripe ? (
    <>
      Something went wrong. Please refresh the page, try a different payment method, or{' '}
      <Link to="/contact" style={{ color: `${theme.colors?.red}` }}>
        contact support
      </Link>
      .
    </>
  ) : null
  const [fullName, setFullName] = useState('')
  const [areCreditCardInputsValid, setAreCreditCardInputsValid] = useState(false)
  const { errorMessage } = useAppSelector(s => s.payments)
  const paymentMethod = useAppSelector(s => s.payments.paymentMethod)
  const {
    priceInterval,
    currentProduct,
    selectedProductPk,
    promoCode,
    error: promoCodeError,
    data: products,
  } = useAppSelector(s => s.products)
  const selectedProduct = products?.find(p => p.PK === selectedProductPk)
  const selectedPrice = selectedProduct?.price_points.find(pp => pp.duration === priceInterval)
  const monthlyPrice = selectedProduct?.price_points.find(
    x => x.duration === CommonPriceInterval.MONTHLY,
  )
  const yearlyPrice = selectedProduct?.price_points.find(
    x => x.duration > CommonPriceInterval.MONTHLY && x.duration % CommonPriceInterval.YEARLY === 0,
  )
  const isAlreadyBilled = selectedPrice?.already_billed === 1
  const is100PercentPromoCodeApplied = selectedPrice?.type === 'promo' && selectedPrice.price === 0
  const isEligibleForTrial = useAppSelector(s => s.trialEligibility.isEligibleForTrial)
  const changeOrUpgradeWithSubscription =
    currentProduct?.price && currentProduct?.subscription?.status

  const shouldForceTrial = useAppSelector(s => s.tracking.shouldForceTrial)

  const someControl = products?.find(x => x.name === ProductType.SOME)

  const [previousSelectedProduct, setPreviousSelectedProduct] = useState<
    BillingProduct | undefined
  >(currentProduct)

  const isPromoCodeApplied =
    products?.[0].price_points?.[0].type === 'promo' && promoCode && !promoCodeError

  useGetProductsQuery({}, { skip: !!products })

  useEffect(() => {
    if (selectedProduct?.price_points.length === 1) {
      dispatch(
        setPriceInterval(
          shouldForceTrial ? CommonPriceInterval.TRIAL : selectedProduct?.price_points[0].duration,
        ),
      )
    }
  }, [dispatch, selectedProduct, shouldForceTrial])

  useEffect(() => {
    if (paymentMethod !== PaymentMethod.CREDIT) {
      setFullName('')
    }
  }, [paymentMethod])

  useEffect(() => {
    // fix an issue with scrolling to top for some mobile devices
    errorMessage && (document.body.scrollTop = document.documentElement.scrollTop = 0)
  }, [errorMessage])

  useEffect(() => {
    if (shouldForceTrial) {
      dispatch(setPriceInterval(CommonPriceInterval.TRIAL))
    }
  }, [dispatch, shouldForceTrial])

  if (!products) {
    return null
  }

  const isSingleColumnView =
    !!changeOrUpgradeWithSubscription ||
    priceInterval === CommonPriceInterval.TRIAL ||
    is100PercentPromoCodeApplied
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        maxWidth: '108rem',
        width: '100%',
        px: '0.4rem',
        justifySelf: 'center',
        textAlign: 'center',
        alignItems: 'center',
      }}
    >
      <TitleSection error={errorMessage || stripeErrorMessage}>
        {changeOrUpgradeWithSubscription ? 'Change Plan' : 'Finish Up'}
      </TitleSection>
      {!changeOrUpgradeWithSubscription &&
        !is100PercentPromoCodeApplied &&
        isEligibleForTrial &&
        !isPromoCodeApplied && (
          <Flex
            sx={{
              width: '100%',
              justifyContent: 'center',
              mb: '4.8rem',
              alignItems: 'center',
            }}
          >
            <Text
              variant="size21Weight600Line138"
              sx={{
                mr: '1.2rem',
                color: 'aliceBlue',
              }}
            >
              30 Days Free Trial?
            </Text>
            <Switch
              data-testid="checkout-free-trial-switch"
              ariaLabel="Free Trial Switch"
              checked={priceInterval === CommonPriceInterval.TRIAL}
              onClick={(): void => {
                dispatch(
                  setPriceInterval(
                    priceInterval === CommonPriceInterval.TRIAL
                      ? getPriceInterval(selectedProduct?.price_points)
                      : CommonPriceInterval.TRIAL,
                  ),
                )
                if (priceInterval === CommonPriceInterval.TRIAL) {
                  if (previousSelectedProduct) {
                    dispatch(setSelectedProductPk(previousSelectedProduct.PK))
                    setPreviousSelectedProduct(undefined)
                  }
                } else {
                  if (someControl) {
                    dispatch(setSelectedProductPk(someControl.PK))
                    setPreviousSelectedProduct(selectedProduct)
                  }
                  dispatch(setPriceInterval(CommonPriceInterval.TRIAL))
                }
              }}
            />
          </Flex>
        )}
      <Flex
        sx={{
          width: ['100%', '74rem', '99rem'],
          flex: '1',
          justifyContent: isSingleColumnView ? 'center' : 'space-between',
          flexDirection: ['column', isSingleColumnView ? 'column' : 'row'],
        }}
      >
        {priceInterval !== CommonPriceInterval.TRIAL &&
          !!selectedPrice?.price &&
          !isAlreadyBilled && (
            <Flex
              sx={{
                width: ['100%', isSingleColumnView ? '100%' : '50%'],
                flexDirection: 'column',
                maxWidth: ['100%', '46.8rem'],
                justifyContent: ['center', changeOrUpgradeWithSubscription ? 'center' : 'left'],
                alignSelf: isSingleColumnView ? 'center' : 'auto',
                alignItems: ['center', 'flex-start'],
                gap: '2.4rem',
              }}
            >
              <Duration yearlyPrice={yearlyPrice} monthlyPrice={monthlyPrice} />
              {!changeOrUpgradeWithSubscription && (
                <PaymentForm
                  fullName={fullName}
                  setFullName={setFullName}
                  setAreCreditCardInputsValid={setAreCreditCardInputsValid}
                />
              )}
            </Flex>
          )}
        <Flex
          sx={{
            justifyContent: isSingleColumnView ? 'center' : ['right', 'right', 'left'],
          }}
        >
          {changeOrUpgradeWithSubscription && priceInterval !== CommonPriceInterval.TRIAL ? (
            <ChangePlanSummary />
          ) : (
            <Summary
              fullName={fullName}
              is100PercentPromoCodeApplied={is100PercentPromoCodeApplied}
              onNextClick={onNextClick}
              areCreditCardInputsValid={areCreditCardInputsValid}
              isAlreadyBilled={isAlreadyBilled}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Checkout
